import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Spinner, Alert, Form, Badge,
} from 'react-bootstrap';
import Layout from '../components/Layout';
import apiService from '../services/api';

const breadcrumbs = [
  { label: 'الرئيسية', path: '/dashboard', icon: 'bi-house' },
  { label: 'الكتّاب', icon: 'bi-person-circle' },
];

const AVATAR_COLORS = [
  '#0d6efd', '#6f42c1', '#d63384', '#dc3545',
  '#fd7e14', '#198754', '#20c997', '#0dcaf0',
];

const avatarColor = (name) =>
  AVATAR_COLORS[(name || '').charCodeAt(2) % AVATAR_COLORS.length];

const ReaderCard = ({ reader }) => {
  const voCount = reader.mjalis_vos?.length   ?? 0;
  const ytCount = reader.mjalis_yts?.length   ?? 0;
  const total   = voCount + ytCount;
  const color   = avatarColor(reader.Name);
  const initials = (reader.Name || '').trim().split(' ').slice(0, 2).map(w => w[0]).join('');

  return (
    <Card className="h-100 border-0 shadow-sm hover-card">
      <Card.Body className="d-flex flex-column align-items-center text-center p-4">
        {/* Avatar */}
        <div
          style={{
            width: 72, height: 72, borderRadius: '50%',
            background: color + '22', border: `2px solid ${color}33`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 16, flexShrink: 0,
          }}
        >
          <span style={{ fontSize: '1.4rem', fontWeight: 700, color }}>{initials}</span>
        </div>

        {/* Name */}
        <Card.Title
          className="mb-3 fw-bold"
          style={{ fontSize: '0.95rem', color: '#212529', lineHeight: 1.4 }}
        >
          {reader.Name}
        </Card.Title>

        {/* Stats */}
        <div className="d-flex gap-2 mt-auto flex-wrap justify-content-center">
          {voCount > 0 && (
            <Badge bg="primary" style={{ fontSize: '0.75rem' }}>
              <i className="bi bi-camera-video ms-1" />
              {voCount} محاضرة
            </Badge>
          )}
          {ytCount > 0 && (
            <Badge bg="danger" style={{ fontSize: '0.75rem' }}>
              <i className="bi bi-youtube ms-1" />
              {ytCount} يوتيوب
            </Badge>
          )}
          {total === 0 && (
            <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>لا توجد محاضرات</Badge>
          )}
        </div>
      </Card.Body>

      {/* Footer total */}
      {total > 0 && (
        <Card.Footer
          className="text-center text-muted border-0 pt-0 pb-3"
          style={{ background: 'transparent', fontSize: '0.8rem' }}
        >
          الإجمالي: {total} تسجيل
        </Card.Footer>
      )}
    </Card>
  );
};

const Readers = () => {
  const [readers, setReaders]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.getReaders({ search });
      setReaders(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'تعذّر تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const totalLectures = readers.reduce(
    (sum, r) => sum + (r.mjalis_vos?.length ?? 0) + (r.mjalis_yts?.length ?? 0), 0
  );

  return (
    <Layout title="الكتّاب" breadcrumbs={breadcrumbs}>
      <Container fluid dir="rtl">

        {/* Stats row */}
        <Row className="mb-4 g-3">
          {[
            { label: 'إجمالي الكتّاب', value: readers.length, icon: 'bi-person-circle', color: '#6f42c1' },
            { label: 'إجمالي التسجيلات', value: totalLectures, icon: 'bi-camera-video-fill', color: '#0d6efd' },
          ].map(s => (
            <Col md={3} sm={6} key={s.label}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="d-flex align-items-center gap-3 p-3">
                  <div style={{
                    width: 54, height: 54, borderRadius: '50%',
                    background: s.color + '22', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className={s.icon} style={{ fontSize: '1.4rem', color: s.color }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1 }}>{s.value}</div>
                    <div className="text-muted small mt-1">{s.label}</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Search */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <Form.Control
              placeholder="بحث باسم القارئ أو الملا..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ maxWidth: 400 }}
            />
          </Card.Body>
        </Card>

        {/* Error */}
        {error && (
          <Alert variant="danger">
            <i className="bi bi-exclamation-triangle-fill ms-2" />
            {error}
          </Alert>
        )}

        {/* Grid */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="text-muted mt-3">جارٍ تحميل الكتّاب...</div>
          </div>
        ) : readers.length === 0 ? (
          <Alert variant="info" className="text-center">
            <i className="bi bi-person-x ms-2" />
            لا توجد نتائج مطابقة
          </Alert>
        ) : (
          <Row className="g-3">
            {readers.map(reader => (
              <Col xl={2} lg={3} md={4} sm={6} key={reader.documentId}>
                <ReaderCard reader={reader} />
              </Col>
            ))}
          </Row>
        )}
      </Container>

      <style>{`
        .hover-card { transition: all 0.25s ease; }
        .hover-card:hover { transform: translateY(-4px); box-shadow: 0 0.5rem 1.5rem rgba(0,0,0,0.12) !important; }
      `}</style>
    </Layout>
  );
};

export default Readers;
